import { AudienceType, SegmentFilter } from '@/types/Notification.interface';

export type CopiedAudience =
  | { type: AudienceType.ALL }
  | { type: AudienceType.SEGMENT; filter: SegmentFilter }
  | { type: AudienceType.INDIVIDUAL; userIds: number[] };

export interface CopiedUser {
  id: number;
  name: string;
  surname: string;
  email?: string;
  phone?: string;
}

const hasValue = (value: unknown) =>
  value !== undefined &&
  value !== null &&
  !(Array.isArray(value) && value.length === 0);

/**
 * A copied notification gets the same audience as its source. The create
 * wizard stores "individual" as a filter with only `includeUserIds`, so that
 * shape goes back to the individual picker; anything else is a segment.
 */
export function copiedAudience(
  filter: SegmentFilter | null | undefined
): CopiedAudience {
  const used = Object.entries(filter ?? {}).filter(([, value]) =>
    hasValue(value)
  );
  if (used.length === 0) return { type: AudienceType.ALL };
  if (used.length === 1 && used[0][0] === 'includeUserIds') {
    return {
      type: AudienceType.INDIVIDUAL,
      userIds: filter!.includeUserIds!
    };
  }
  return {
    type: AudienceType.SEGMENT,
    filter: Object.fromEntries(used) as SegmentFilter
  };
}

/**
 * Picker chips for the copied individual audience, named from the source's
 * recipient list. A user missing there (beyond the first page) still stays
 * in the audience, shown by id.
 */
export function individualUsersFrom(
  userIds: number[],
  recipients: Array<{ userId?: number; user?: Partial<CopiedUser> | null }>
): CopiedUser[] {
  const byId = new Map<number, Partial<CopiedUser>>();
  for (const recipient of recipients) {
    const id = recipient.user?.id ?? recipient.userId;
    if (typeof id === 'number' && recipient.user) byId.set(id, recipient.user);
  }
  return userIds.map((id) => {
    const user = byId.get(id);
    return {
      id,
      name: user?.name ?? `#${id}`,
      surname: user?.surname ?? '',
      ...(user?.email ? { email: user.email } : {}),
      ...(user?.phone ? { phone: user.phone } : {})
    };
  });
}
